import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ChevronDown } from "lucide-react";
import { fetchMachine, resolveMediaUrl } from "@/lib/api";
import { MACHINE_DETAIL } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import SEO from "@/components/SEO";
import { productLd, breadcrumbLd } from "@/lib/jsonLd";

export default function MachineDetail() {
  const { slug } = useParams();
  const [machine, setMachine] = useState(null);
  const [tab, setTab] = useState("specs");
  const [openFaq, setOpenFaq] = useState(0);
  const { t } = useT();

  useEffect(() => {
    setMachine(null);
    fetchMachine(slug).then(setMachine).catch(() => {});
  }, [slug]);

  if (!machine) {
    return <div className="min-h-screen flex items-center justify-center text-black/60">{t("common.loading")}</div>;
  }

  return (
    <div data-testid={MACHINE_DETAIL.container}>
      <SEO
        title={`${machine.name} · Equipo Dermo Facial Profesional | Asirox`}
        description={`${machine.name}: ${machine.tagline}. ${machine.short_description?.slice(0, 150) || ""}`}
        keywords={`${machine.name.toLowerCase()}, equipo dermo facial, plataforma dermo facial profesional, ${machine.category?.toLowerCase()}, aparatología estética, ${machine.applications?.slice(0,3).join(", ").toLowerCase()}, Asirox`}
        ogImage={resolveMediaUrl(machine.hero_image)}
        jsonLd={[
          productLd(machine),
          breadcrumbLd([
            { name: "Inicio", url: "/" },
            { name: "Equipos dermo faciales", url: "/equipos-dermo-faciales" },
            { name: machine.name, url: `/equipos-dermo-faciales/${machine.slug}` },
          ]),
        ]}
      />
      {/* HERO PRODUCT (dark) */}
      <section className="relative pt-16 pb-20 overflow-hidden dark-section">
        <div className="absolute inset-0 opacity-30">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
            <source src={resolveMediaUrl(machine.video_url)} type="video/mp4"/>
          </video>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black"/>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 pt-10">
          <div className="md:col-span-7">
            <Link to="/equipos-dermo-faciales" className="overline link-underline">← {t("nav.machines")}</Link>
            <p className="overline mt-6">{machine.category}</p>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter mt-4 leading-[0.95] text-white">
              {machine.name}
            </h1>
            <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl leading-relaxed">{machine.tagline}</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link to={`/contacto?intent=demo&modelo=${machine.slug}`} data-testid={MACHINE_DETAIL.cta_demo} className="btn-primary">
                {t("hero.cta_demo")} <ArrowUpRight size={18}/>
              </Link>
              <Link to={`/contacto?intent=quote&modelo=${machine.slug}`} data-testid={MACHINE_DETAIL.cta_quote} className="btn-secondary btn-on-dark">
                {t("hero.cta_quote")}
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 grid grid-cols-2 gap-3 self-end">
            {[
              { l: "Longitud de onda", v: machine.wavelength },
              { l: "Potencia", v: machine.power },
              { l: "Spot", v: machine.spot_size },
              { l: "Refrigeración", v: machine.cooling },
            ].map((c) => (
              <div key={c.l} className="border border-white/15 p-4 bg-black/30 backdrop-blur-md">
                <p className="overline text-white/50">{c.l}</p>
                <p className="font-mono text-sm text-white mt-2">{c.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LONG DESCRIPTION (light) */}
      <section className="py-24 border-b border-black/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline">Descripción técnica</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-4 ink-heading">Un equipo diseñado para perdurar.</h2>
          <p className="text-black leading-relaxed mt-8 text-lg">{machine.long_description}</p>
        </div>
      </section>

      {/* TABS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 border-b border-black/15 mb-12">
            {[
              { k: "specs", l: "Especificaciones", testid: MACHINE_DETAIL.tab_specs },
              { k: "applications", l: "Aplicaciones & Beneficios", testid: MACHINE_DETAIL.tab_applications },
              { k: "tech", l: "Tecnologías", testid: "machine-tab-tech" },
              { k: "faq", l: "Preguntas frecuentes", testid: MACHINE_DETAIL.tab_faq },
            ].map((tt) => (
              <button
                key={tt.k}
                data-testid={tt.testid}
                onClick={() => setTab(tt.k)}
                className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition ${
                  tab === tt.k ? "border-[#ff9c00] text-[#3D1F0A]" : "border-transparent text-black/60 hover:text-black"
                }`}
              >
                {tt.l}
              </button>
            ))}
          </div>

          {tab === "specs" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
              {machine.specs?.map((s) => (
                <div key={s.label} className="flex justify-between py-3 border-b border-black/10">
                  <span className="text-black/70 text-sm">{s.label}</span>
                  <span className="text-[#3D1F0A] font-mono text-sm font-bold">{s.value}</span>
                </div>
              ))}
            </div>
          )}

          {tab === "applications" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <p className="overline mb-6">Aplicaciones</p>
                <ul className="space-y-3">
                  {machine.applications?.map((a) => (
                    <li key={a} className="flex gap-3 text-black">
                      <Check size={18} strokeWidth={1.8} className="text-[#3D1F0A] shrink-0 mt-0.5"/>
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="overline mb-6">Beneficios</p>
                <ul className="space-y-3">
                  {machine.benefits?.map((b) => (
                    <li key={b} className="flex gap-3 text-black">
                      <Check size={18} strokeWidth={1.8} className="text-[#3D1F0A] shrink-0 mt-0.5"/>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {tab === "tech" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {machine.technologies?.map((tech, i) => (
                <div key={tech.name} className="border border-black/15 bg-[#f7ebd9] p-6 card-tilt">
                  <p className="font-mono text-xs text-[#3D1F0A]">T0{i+1}</p>
                  <p className="font-heading text-xl font-bold mt-3 ink-heading">{tech.name}</p>
                  <p className="text-black/75 mt-3 leading-relaxed text-sm">{tech.description}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "faq" && (
            <div className="space-y-3 max-w-3xl">
              {machine.faqs?.map((f, i) => (
                <div key={i} className="border border-black/15 bg-[#f7ebd9]">
                  <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex justify-between items-center p-5 text-left">
                    <span className="font-semibold text-black">{f.q}</span>
                    <ChevronDown size={18} className={`text-[#3D1F0A] transition-transform ${openFaq === i ? "rotate-180" : ""}`}/>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-black/80 leading-relaxed">{f.a}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-[#f1e3cb] border-t border-black/10">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter ink-heading">
            ¿Listo para conocer <span className="text-black">{machine.name}</span> en vivo?
          </h2>
          <p className="text-black/75 mt-6 max-w-xl mx-auto">Agenda una demo presencial o videollamada técnica con un ingeniero Asirox. Recibirás también un análisis de ROI específico para tu centro.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
            <Link to={`/contacto?intent=demo&modelo=${machine.slug}`} className="btn-primary">Reservar demo</Link>
            <Link to="/equipos-dermo-faciales" className="btn-secondary">Ver otros equipos</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
